import { calculateFutureDate, ChangeOrder, ChangeOrderStatus } from "@oneethos/shared"
import { useState } from "react"
import { toast } from 'react-toastify'
import api from '../api-client'
import { errorToString } from "@oneethos/shared"
import { Spinner } from "@fluentui/react"

const getStatusBadgeClass = (status: ChangeOrderStatus) => {
  switch (status) {
    case ChangeOrderStatus.Pending:
      return 'badge bg-secondary text-capitalize'
    case ChangeOrderStatus.Approved:
      return 'badge bg-success text-capitalize'
    case ChangeOrderStatus.Rejected:
      return 'badge bg-secondary text-capitalize'
    case ChangeOrderStatus.Cancelled:
      return 'badge bg-secondary text-capitalize'
    default:
      return 'badge bg-secondary text-capitalize'
  }
}

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

type ChangeOrderSummaryProps = {
  changeOrder: ChangeOrder
  isLatest?: boolean
  onResend: (coid: string) => void
  resending: boolean
}

export const ChangeOrderSummary = ({ changeOrder, isLatest = false, onResend, resending }: ChangeOrderSummaryProps) => {
  const [downloading, setDownloading] = useState(false)
  const isPending = changeOrder.status === ChangeOrderStatus.Pending
  const isApproved = changeOrder.status === ChangeOrderStatus.Approved
  
  // Use lastResentAt if available, otherwise fall back to createdDate
  const effectiveSentDate = changeOrder.lastResentAt || changeOrder.createdDate
  const effectiveSentDateObj = effectiveSentDate ? new Date(effectiveSentDate) : null
  const changeOrderExpiresDate = effectiveSentDate ? calculateFutureDate(effectiveSentDate, 7) : null
  const hasFifteenMinutesPassed = effectiveSentDateObj
    ? Date.now() - effectiveSentDateObj.getTime() >= FIFTEEN_MINUTES_MS : false
  const shouldResend = isLatest && isPending && hasFifteenMinutesPassed

  return (
    <div className="list-group-item d-flex justify-content-between gap-3">
      <div>
        <div className="mb-3">
          <span className={getStatusBadgeClass(changeOrder.status)}>
            {changeOrder.status}
          </span>
        </div>
        <div>
          {changeOrder.systemSizeKW && (
            <div className="mb-1">
              <strong>System Size:</strong> {changeOrder.systemSizeKW} kW
            </div>
          )}

          {changeOrder.solarCost !== 0 && (
            <div className="mb-1">
              <strong>Solar Cost:</strong> ${changeOrder.solarCost.toLocaleString()}
            </div>
          )}

          {changeOrder.storageQty && (
            <div className="mb-1">
              <strong>Storage Quantity:</strong> {changeOrder.storageQty}
            </div>
          )}

          {changeOrder.teslaPowerwallQty && (
            <div className="mb-1">
              <strong>Tesla Powerwall Quantity:</strong> {changeOrder.teslaPowerwallQty}
            </div>
          )}

          {changeOrder.storageCapacity && (
            <div className="mb-1">
              <strong>Storage Capacity:</strong> {changeOrder.storageCapacity}
            </div>
          )}

          {changeOrder.installerNotes && (
            <div className="mb-1 mt-2">
              <strong>Notes:</strong> {changeOrder.installerNotes}
            </div>
          )}

          {changeOrder.isPostNTP && (
            <div className="mb-1">
              <strong>Post-NTP:</strong> Yes
              {changeOrder.loanModFee && (
                <span className="ms-2">
                  (Fee Covered by: {changeOrder.feeCoveredBy})
                </span>
              )}
            </div>
          )}

          {changeOrder.loanModForm && (
            downloading ? <Spinner /> : <a href='#'
              onClick={(ev) => {
                ev.preventDefault()
                setDownloading(true)
                api.authenticatedFetch(`/loanapps/${changeOrder.loanApplicationId}/change-orders/${changeOrder._id}/loanmodform`, {
                  responseType: 'blob'
                }).then(async res => {
                  // const disposition = res.headers.get('Content-Disposition')
                  // const filename = disposition.split('filename=')[1].replace(/"/g, '')
                  const blob = await res.blob()
                  // fileDownload(blob, filename)
                  window.open(URL.createObjectURL(blob), '_blank')
                }).catch(ex => toast.error(errorToString(ex)))
                  .finally(() => setDownloading(false))
              }}
            >View Loan Modification Form</a>
          )}
        </div>
      </div>
      <div className="d-flex flex-column justify-content-between gap-3">
        <div className="d-flex flex-column">
          <small className="text-muted mb-2">
            <b>Sent:</b> {formatDate(new Date(changeOrder.createdDate))}
          </small>
          {changeOrder.lastResentAt && (
            <small className="text-muted mb-2">
              <b>Last Resent:</b> {formatDate(new Date(changeOrder.lastResentAt))}
            </small>
          )}
          {isLatest && !isApproved && (
            <small className="text-muted">
              <b>Expires:</b> {formatDate(changeOrderExpiresDate)}
            </small>
          )}
        </div>
        {shouldResend && (
          <button className="btn btn-sm btn-primary align-self-end" onClick={() => onResend(changeOrder._id.toString())}>
            {resending ? <Spinner /> : 'Resend'}
          </button>
        )}
      </div>
    </div>
  )
}